import { animate, style, transition, trigger } from "@angular/animations";
import { CommonModule } from "@angular/common";
import { NgIconComponent } from "@ng-icons/core";
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { overlayAnchor, topOverlayZIndex } from "../overlay-anchor";

/**
 * Interface pour un item de menu
 */
export interface EvaMenuItem {
  label?: string; // Optionnel pour les séparateurs
  icon?: string;
  description?: string; // Texte descriptif affiché sous le label
  command?: () => void;
  disabled?: boolean;
  separator?: boolean;
  sectionHeader?: boolean; // En-tête de section (non cliquable, fond gris)
  severity?: "danger"; // Pour les items destructifs
  items?: EvaMenuItem[]; // Sous-menu (non implémenté pour l'instant)
}

/**
 * EVA Menu - Menu contextuel / dropdown
 *
 * Composant 100% custom utilisant les tokens EVA.
 * Le dropdown est en position fixed par défaut (`appendTo="body"`) : une colonne
 * d'actions vit presque toujours dans un `eva-table` (`overflow-x: auto`, donc
 * `overflow-y: auto` calculé) niché dans une `eva-card` (`overflow: clip`), qui
 * découpent tous deux un dropdown en position absolute.
 * Supporte click outside pour fermer.
 *
 * @example
 * <eva-menu [items]="menuItems">
 *   <ng-template #menuTrigger>
 *     <eva-button icon="pi pi-ellipsis-v" variant="text"></eva-button>
 *   </ng-template>
 * </eva-menu>
 *
 * // Dans le composant :
 * menuItems: EvaMenuItem[] = [
 *   { label: 'Modifier', icon: 'pi pi-pencil', command: () => this.edit() },
 *   { label: 'Dupliquer', icon: 'pi pi-copy', command: () => this.duplicate() },
 *   { separator: true },
 *   { label: 'Supprimer', icon: 'pi pi-trash', severity: 'danger', command: () => this.delete() }
 * ];
 *
 * // Ancré sur un élément extérieur, ou ouvert au curseur sur un clic droit
 * <eva-button icon="pi pi-ellipsis-v" (onClick)="menu.toggle($event)"></eva-button>
 * <div (contextmenu)="menu.open($event)">…</div>
 * <eva-menu #menu [items]="menuItems"></eva-menu>
 */
@Component({
  selector: "eva-menu",
  standalone: true,
  imports: [CommonModule, NgIconComponent],
  templateUrl: "./eva-menu.component.html",
  styleUrls: ["./eva-menu.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger("dropdownAnimation", [
      transition(":enter", [
        style({ opacity: 0, transform: "translateY(-8px)" }),
        animate("150ms ease-out", style({ opacity: 1, transform: "translateY(0)" })),
      ]),
      transition(":leave", [animate("100ms ease-in", style({ opacity: 0, transform: "translateY(-8px)" }))]),
    ]),
  ],
})
export class EvaMenuComponent implements OnChanges, AfterViewChecked, OnDestroy {
  // ==========================================================================
  // Inputs
  // ==========================================================================

  /** Liste des items du menu */
  @Input() items: EvaMenuItem[] = [];

  /** Déclencheur d'ouverture */
  @Input() trigger: "click" | "hover" = "click";

  /** Position du dropdown */
  @Input() position: "bottom-left" | "bottom-right" | "top-left" | "top-right" = "bottom-left";

  /**
   * Sort le dropdown du flux pour échapper aux ancêtres qui le découpent (overflow, z-index).
   * `"body"` (défaut) = position fixed sur coordonnées viewport ; `""` = position absolute.
   *
   * Ne repasser à `""` que si le menu vit dans un conteneur qui établit un bloc conteneur
   * pour les éléments fixed (`transform`, `will-change`, `filter`, `contain`) : les
   * coordonnées viewport y sont alors interprétées relativement à ce conteneur.
   */
  @Input() appendTo: "" | "body" = "body";

  // ==========================================================================
  // Content Children
  // ==========================================================================

  /** Template pour le déclencheur */
  @ContentChild("menuTrigger") menuTrigger: TemplateRef<unknown> | null = null;

  /** Référence au dropdown pour le positionnement fixed */
  @ViewChild("dropdownEl") private dropdownEl?: ElementRef<HTMLDivElement>;

  // ==========================================================================
  // State
  // ==========================================================================

  isOpen: boolean = false;
  /** Élément extérieur sur lequel le menu est ancré (ouvert via `toggle($event)` / `open($event)`) */
  anchor: Element | null = null;
  private anchorPoint: { x: number; y: number } | null = null;
  private dropdownHome: { parent: HTMLElement; el: HTMLElement } | null = null;
  private repositionPending = false;
  private clickListener: ((event: Event) => void) | null = null;
  private repositionListener: (() => void) | null = null;

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {}

  // ==========================================================================
  // Lifecycle
  // ==========================================================================

  // Le parent remplit souvent `items` puis appelle open() : l'input arrive au tick suivant,
  // après un premier positionnement calculé sur l'ancienne liste.
  ngOnChanges(changes: SimpleChanges): void {
    if (changes["items"] && this.isOpen) this.repositionPending = true;
  }

  ngAfterViewChecked(): void {
    if (!this.repositionPending) return;
    this.repositionPending = false;
    if (this.isOpen && (this.appendTo === "body" || this.anchor)) this.positionDropdown();
  }

  ngOnDestroy(): void {
    this.removeClickListener();
    this.removeRepositionListener();
    this.restoreDropdown();
  }

  // ==========================================================================
  // Host Listeners
  // ==========================================================================

  // En capture : eva-dialog stoppe la propagation du clic et d'Escape avant le document.
  private readonly escapeCaptureListener = (event: KeyboardEvent): void => {
    if (event.key !== "Escape" || !this.isOpen) return;
    event.preventDefault();
    event.stopPropagation();
    this.onEscape();
  };

  /**
   * Ferme le menu avec Escape
   */
  onEscape(): void {
    const focusInMenu = !!this.dropdownEl?.nativeElement.contains(document.activeElement);
    const anchor = this.anchor;
    this.close();
    if (focusInMenu) {
      const host = this.elementRef.nativeElement as HTMLElement;
      const trigger =
        anchor instanceof HTMLElement
          ? anchor
          : host.querySelector<HTMLElement>(".eva-menu__trigger button, .eva-menu__trigger [tabindex]");
      trigger?.focus();
    }
  }

  // ==========================================================================
  // Click Outside
  // ==========================================================================

  /**
   * Ajoute un listener document pour fermer au clic en dehors
   */
  private addClickListener(): void {
    // setTimeout pour ne pas capturer le clic d'ouverture
    setTimeout(() => {
      if (!this.isOpen || this.clickListener) return;
      this.clickListener = (event: Event) => {
        const target = event.target as HTMLElement;
        if (this.elementRef.nativeElement.contains(target)) return;
        if (this.dropdownEl?.nativeElement.contains(target)) return;
        if (!this.anchorPoint && this.anchor?.contains(target)) return;
        this.close();
      };
      document.addEventListener("click", this.clickListener, true);
    });
    document.addEventListener("keydown", this.escapeCaptureListener, true);
  }

  /**
   * Supprime le listener document
   */
  private removeClickListener(): void {
    document.removeEventListener("keydown", this.escapeCaptureListener, true);
    if (this.clickListener) {
      document.removeEventListener("click", this.clickListener, true);
      this.clickListener = null;
    }
  }

  // ==========================================================================
  // Scroll / Resize
  // ==========================================================================

  /**
   * En position fixed, les coordonnées sont figées à l'ouverture : un scroll ou un
   * resize qui déplace le trigger détacherait visuellement le dropdown. On ferme.
   * Capture activée pour attraper aussi le scroll des conteneurs internes (tableaux).
   */
  private addRepositionListener(): void {
    const anchor = this.triggerRect();
    this.repositionListener = () => {
      const rect = this.triggerRect();
      // Un scroll d'un autre conteneur, ou livré en retard (onglet caché), ne déplace pas le trigger.
      if (!anchor || !rect || Math.abs(rect.top - anchor.top) > 1 || Math.abs(rect.left - anchor.left) > 1) {
        this.close();
      }
    };
    window.addEventListener("scroll", this.repositionListener, true);
    window.addEventListener("resize", this.repositionListener);
  }

  private triggerRect(): DOMRect | undefined {
    const trigger = this.anchor ?? (this.elementRef.nativeElement as HTMLElement).querySelector(".eva-menu__trigger");
    return trigger?.getBoundingClientRect();
  }

  /** Rectangle d'où part le dropdown : le point du clic droit, sinon l'ancre ou le trigger. */
  private placementRect(): DOMRect | undefined {
    if (this.anchorPoint) return new DOMRect(this.anchorPoint.x, this.anchorPoint.y, 0, 0);
    return this.triggerRect();
  }

  private removeRepositionListener(): void {
    if (this.repositionListener) {
      window.removeEventListener("scroll", this.repositionListener, true);
      window.removeEventListener("resize", this.repositionListener);
      this.repositionListener = null;
    }
  }

  // ==========================================================================
  // Methods
  // ==========================================================================

  /** Avec un événement venu d'un autre élément que l'ancre courante, le menu s'y déplace. */
  toggle(event?: Event): void {
    const sameAnchor = !event || (event.type !== "contextmenu" && overlayAnchor(event) === this.anchor);
    if (this.isOpen && sameAnchor) {
      this.close();
    } else {
      this.open(event);
    }
  }

  /** Avec un événement, s'ancre sur l'élément cliqué ; un `contextmenu` l'ouvre au curseur. */
  open(event?: Event): void {
    if (this.isOpen && !event) return;

    if (event) {
      this.anchor = overlayAnchor(event);
      this.anchorPoint = null;
      if (event.type === "contextmenu" && event instanceof MouseEvent) {
        event.preventDefault();
        this.anchorPoint = { x: event.clientX, y: event.clientY };
      }
    }

    if (this.isOpen) {
      this.removeRepositionListener();
      if (this.anchor) this.moveDropdownToBody();
      this.positionDropdown();
      this.addRepositionListener();
      return;
    }

    this.isOpen = true;
    this.addClickListener();
    this.cdr.markForCheck();
    if (this.appendTo === "body" || this.anchor) {
      this.cdr.detectChanges();
      if (this.anchor) this.moveDropdownToBody();
      this.positionDropdown();
      this.addRepositionListener();
    }
  }

  /**
   * Ferme le menu
   */
  close(): void {
    if (!this.isOpen) return;

    this.restoreDropdown();
    this.anchor = null;
    this.anchorPoint = null;
    this.isOpen = false;
    this.removeClickListener();
    this.removeRepositionListener();
    this.cdr.markForCheck();
  }

  /** Ancré hors de son hôte, le dropdown doit échapper au stacking context de celui-ci. */
  private moveDropdownToBody(): void {
    const el = this.dropdownEl?.nativeElement;
    if (!el?.parentElement || el.parentElement === document.body) return;
    this.dropdownHome = { parent: el.parentElement, el };
    document.body.appendChild(el);
    el.style.zIndex = String(topOverlayZIndex());
  }

  // Angular retire le dropdown via son parent d'origine : il doit y être revenu avant.
  private restoreDropdown(): void {
    const home = this.dropdownHome;
    if (home && home.el.parentElement === document.body) home.parent.appendChild(home.el);
    this.dropdownHome = null;
  }

  /**
   * Gère le clic sur un item
   */
  private activateItem(item: EvaMenuItem): void {
    if (item.disabled || item.separator) return;

    this.close();
    item.command?.();
  }

  private positionDropdown(): void {
    const el = this.dropdownEl?.nativeElement;
    if (!el) return;
    const rect = this.placementRect();
    if (!rect) return;
    const height = el.offsetHeight;
    const width = el.offsetWidth;
    const gap = 4;

    // Flip vers le côté opposé si la position demandée sort du viewport et que
    // l'autre côté a la place : sans ça, le menu de la dernière ligne d'un
    // tableau s'ouvre sous le bas de l'écran et ses actions sont inatteignables.
    let openDown = this.position === "bottom-left" || this.position === "bottom-right";
    if (openDown) {
      if (rect.bottom + gap + height > window.innerHeight && rect.top - gap - height >= 0) openDown = false;
    } else if (rect.top - gap - height < 0 && rect.bottom + gap + height <= window.innerHeight) {
      openDown = true;
    }

    let alignRight = this.position === "bottom-right" || this.position === "top-right";
    if (alignRight) {
      if (rect.right - width < 0) alignRight = false;
    } else if (rect.left + width > window.innerWidth) {
      alignRight = true;
    }

    // Les coordonnées sont relatives au bloc conteneur, pas forcément au viewport.
    const block = this.getFixedContainingBlock(el);
    const originTop = block ? block.top : 0;
    const originLeft = block ? block.left : 0;
    const edgeBottom = block ? block.bottom : window.innerHeight;
    const edgeRight = block ? block.right : window.innerWidth;

    el.style.position = "fixed";

    if (openDown) {
      el.style.top = `${rect.bottom + gap - originTop}px`;
      el.style.bottom = "auto";
    } else {
      el.style.bottom = `${edgeBottom - rect.top + gap}px`;
      el.style.top = "auto";
    }

    if (alignRight) {
      el.style.right = `${edgeRight - rect.right}px`;
      el.style.left = "auto";
    } else {
      el.style.left = `${rect.left - originLeft}px`;
      el.style.right = "auto";
    }
  }

  /**
   * Un ancêtre avec transform / perspective / filter / will-change / contain devient le
   * bloc conteneur des descendants `position: fixed` : les coordonnées viewport y sont
   * réinterprétées relativement à sa padding box.
   *
   * Aucun conteneur EVA n'est dans ce cas de façon permanente aujourd'hui — mais les
   * animations d'entrée (`.animate-entrance`, `eva-dialog-in`) posent un transform
   * pendant leur durée, et `.eva-table--virtual-scroll .eva-table__wrapper` porte un
   * `will-change: transform` permanent. Sans compensation, un menu ouvert dans ces
   * fenêtres-là atterrit à côté.
   *
   * Ne gère que la translation : sous un ancêtre à l'échelle ou en rotation le calcul
   * reste approximatif (cas absent d'EVA, et transitoire quand il apparaît).
   *
   * Retourne la padding box de cet ancêtre, ou null si le viewport fait référence.
   */
  private getFixedContainingBlock(el: HTMLElement): DOMRect | null {
    let parent = el.parentElement;

    while (parent && parent !== document.body) {
      const style = getComputedStyle(parent);
      const establishesBlock =
        style.transform !== "none" ||
        style.perspective !== "none" ||
        style.filter !== "none" ||
        /transform|perspective|filter/.test(style.willChange) ||
        /paint|layout|strict|content/.test(style.contain);

      if (establishesBlock) {
        const rect = parent.getBoundingClientRect();
        return new DOMRect(
          rect.left + parent.clientLeft,
          rect.top + parent.clientTop,
          parent.clientWidth,
          parent.clientHeight,
        );
      }

      parent = parent.parentElement;
    }

    return null;
  }

  isClassIcon(icon: string): boolean {
    return icon.startsWith("pi ");
  }

  onItemPointerDown(item: EvaMenuItem, event: PointerEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.activateItem(item);
  }

  onItemKeydown(item: EvaMenuItem, event: KeyboardEvent): void {
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      this.moveFocus(event.currentTarget as HTMLElement, event.key);
      return;
    }
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    event.stopPropagation();
    this.activateItem(item);
  }

  private moveFocus(from: HTMLElement, key: string): void {
    const menu = from.closest(".eva-menu__dropdown");
    const items = Array.from(menu?.querySelectorAll<HTMLButtonElement>(".eva-menu__item:not(:disabled)") ?? []);
    if (!items.length) return;

    const index = items.indexOf(from as HTMLButtonElement);
    const last = items.length - 1;
    const next =
      key === "Home"
        ? 0
        : key === "End"
          ? last
          : key === "ArrowDown"
            ? (index + 1) % items.length
            : (index - 1 + items.length) % items.length;
    items[next].focus();
  }

  /**
   * Gère le mouseenter pour trigger hover
   */
  onMouseEnter(): void {
    if (this.trigger === "hover") {
      this.open();
    }
  }

  /**
   * Gère le mouseleave pour trigger hover
   */
  onMouseLeave(): void {
    if (this.trigger === "hover") {
      this.close();
    }
  }
}
